/**
 * TutorMessageBubble — renders one tutor-conversation turn (user or
 * assistant), the assistant's citation "marginalia" (deep-links back to the
 * manual — TASK-010's actual moat), an inline action card when the server
 * asked for one, and the 👍/👎 feedback row.
 *
 * The assistant's `content` is rendered as Markdown (see ./TutorMarkdown.js)
 * — Claude's responses come back as Markdown prose (bold, lists, ...) and
 * were previously shown as a raw, unrendered string. The student's own
 * message is always plain text (their literal asterisks aren't Markdown).
 *
 * Pure presentational component — all navigation/action decisions are made
 * by the caller (TutorOverlay.tsx) via the two callback props; this file has
 * no knowledge of Inertia, the reader TOC, or the quiz flow.
 *
 * The view-model types (`TutorCitationView`, `TutorActionView`,
 * `TutorViewMessage`) moved to `@arianna/reader-core/tutor` in TASK-019
 * Phase 4 ("Package condivisi — estrazione tutor") — re-exported below so no
 * other file in this app has to change its import.
 */

import * as React from 'react'
import Text from '@arianna/design-system/Text'
import type { ToolStepName } from '../tutorStream.js'
import type {
  TutorActionView,
  TutorCitationView,
  TutorViewMessage,
} from '@arianna/reader-core/tutor'
import { TutorInlineMarkdown, TutorMarkdown } from './TutorMarkdown.js'

export type {
  TutorActionView,
  TutorCitationView,
  TutorViewMessage,
} from '@arianna/reader-core/tutor'

/**
 * Italian, per-tool copy for the `tool_step` indicator (TASK-012 Phase C) —
 * shown while `message.pending && message.toolStep != null` (see render
 * below). Keyed by the exact `Tutor::Toolbox` tool names (tutorStream.ts's
 * `TOOL_STEP_NAMES`), so a missing case here is a TS error, not a silent
 * blank indicator.
 */
const TOOL_STEP_COPY: Record<ToolStepName, string> = {
  search_manual: 'Cerco nel manuale…',
  get_quiz_results: 'Controllo i tuoi risultati…',
  get_mastery: 'Valuto la tua padronanza…',
  launch_quiz: 'Preparo il quiz…',
  open_section: 'Apro la sezione…',
}

export interface TutorMessageBubbleProps {
  message: TutorViewMessage
  onOpenCitation: (citation: TutorCitationView) => void
  onRunAction: (action: TutorActionView) => void
  onFeedback: (value: 'up' | 'down') => void
  /**
   * Compact layout for TutorDrawer's ~400px panel: the sender is a small
   * label ABOVE the bubble instead of a lateral avatar (recovers the
   * avatar's ~36px of horizontal width), bubbles run wider (full width for
   * the assistant), and padding/gaps tighten up. Default false — identical
   * to the pre-existing layout, used unchanged by TutorOverlay's
   * full-screen column.
   */
  compact?: boolean
}

function TutorAvatar() {
  return (
    <div
      className="flex items-center justify-center size-7 rounded-full bg-accent-bg border border-accent text-accent shrink-0"
      aria-hidden="true"
    >
      {/* Quill — the manual is hand-rewritten, not a mascot */}
      <svg
        width="14"
        height="14"
        viewBox="0 0 22 22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 3c-5 0-9 4-11 9l-3 7 7-3c5-2 9-6 9-11z" />
        <path d="M9 13l6-6" />
      </svg>
    </div>
  )
}

function ActionIcon({ kind }: { kind: TutorActionView['kind'] }) {
  if (kind === 'launch_quiz') {
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 22 22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M5 4h9l3 3v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
        <path d="M14 4v3h3" />
        <path d="M8 11h6M8 14h6" />
      </svg>
    )
  }
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4h5v14H4z" />
      <path d="M11 4h3v14h-3z" />
      <path d="M16 5l4 1-2 12-4-1z" />
    </svg>
  )
}

function TutorActionCard({
  action,
  onRun,
}: {
  action: TutorActionView
  onRun: (action: TutorActionView) => void
}) {
  if (action.kind === 'launch_quiz_unavailable') {
    return (
      <div className="rounded-md border border-line bg-surface-2 px-3 py-2 text-xs text-ink-3 font-sans max-w-full">
        Un quiz proposto in questa conversazione non è più ripartibile da qui — avvialo dal pulsante
        Quiz nel manuale.
      </div>
    )
  }

  const label =
    action.kind === 'launch_quiz' ? 'Avvia il quiz proposto' : 'Apri la sezione nel manuale'

  return (
    <button
      onClick={() => onRun(action)}
      className="inline-flex items-center gap-2 rounded-md border border-accent bg-accent-bg px-3 py-2 text-sm font-medium text-accent cursor-pointer hover:opacity-90 transition-opacity max-w-full"
    >
      <ActionIcon kind={action.kind} />
      <span className="truncate">{label}</span>
      <span aria-hidden="true">→</span>
    </button>
  )
}

function FeedbackButton({
  kind,
  active,
  onClick,
}: {
  kind: 'up' | 'down'
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      title={kind === 'up' ? 'Risposta utile' : 'Risposta non utile'}
      aria-pressed={active}
      className={`inline-flex items-center justify-center size-6 rounded border-none cursor-pointer transition-colors ${
        active ? 'text-accent bg-accent-bg' : 'text-ink-4 bg-transparent hover:text-ink-3'
      }`}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill={active ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={kind === 'down' ? { transform: 'scaleY(-1)' } : undefined}
      >
        <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3zm0 0l4-8a2 2 0 0 1 3.6 1.7L13.5 9H19a2 2 0 0 1 2 2.3l-1.3 7A2 2 0 0 1 17.7 20H9a2 2 0 0 1-2-2v-7z" />
      </svg>
    </button>
  )
}

/** Citations beyond this count collapse behind a "Mostra altre" toggle — a
 *  message with, say, 8 citations reading as a wall of footnote cards was
 *  worse than the deep-links they're meant to make it easy to verify. */
const CITATION_COLLAPSE_THRESHOLD = 3

export function TutorMessageBubble({
  message,
  onOpenCitation,
  onRunAction,
  onFeedback,
  compact = false,
}: TutorMessageBubbleProps) {
  const isUser = message.role === 'user'
  // Per-message UI-only state (not persisted, not lifted to TutorOverlay) —
  // each bubble instance owns whether ITS OWN citation list is expanded.
  const [citationsExpanded, setCitationsExpanded] = React.useState(false)
  const hasMoreCitations = message.citations.length > CITATION_COLLAPSE_THRESHOLD
  const visibleCitations =
    citationsExpanded || !hasMoreCitations
      ? message.citations
      : message.citations.slice(0, CITATION_COLLAPSE_THRESHOLD)

  return (
    <div className={compact ? 'w-full' : `flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!compact && !isUser && <TutorAvatar />}
      <div
        className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2'} ${
          compact ? 'w-full' : 'max-w-[72%]'
        } ${isUser ? 'items-end' : 'items-start'}`}
      >
        {compact && (
          <Text
            variant="meta"
            className="text-ink-3 uppercase tracking-[0.08em] text-[10px] font-semibold px-0.5"
          >
            {isUser ? 'Tu' : 'Tutor'}
          </Text>
        )}
        <div
          className={
            isUser
              ? `rounded-lg bg-ink ${compact ? 'px-3 py-2 max-w-[88%]' : 'px-4 py-2'}`
              : `rounded-lg bg-surface border border-line ${compact ? 'px-3 py-2 w-full' : 'px-4 py-3'}`
          }
        >
          {isUser ? (
            // The student's own text is never interpreted as Markdown — no
            // reason to parse their literal asterisks/underscores.
            <Text variant="ui" className="whitespace-pre-wrap text-bg">
              {message.content}
            </Text>
          ) : (
            // Not wrapped in <Text>: Text's "body" variant renders a <span>,
            // and Markdown output (<ul>, <table>, ...) is block-level —
            // nesting block elements inside an inline <span> is invalid HTML.
            <div>
              <TutorMarkdown content={message.content} />
              {message.pending && (
                <span
                  aria-hidden="true"
                  className="inline-block w-[2px] h-[1em] bg-ink-3 ml-0.5 align-middle animate-pulse"
                />
              )}
            </div>
          )}
        </div>

        {!isUser && message.pending && message.toolStep != null && (
          <div className="flex items-center gap-1.5 px-1" aria-live="polite">
            <span
              aria-hidden="true"
              className="inline-block size-1.5 rounded-full bg-accent animate-pulse"
            />
            <Text variant="meta" className="text-ink-3">
              {TOOL_STEP_COPY[message.toolStep]}
            </Text>
          </div>
        )}

        {message.citations.length > 0 && (
          <div className="flex flex-col gap-1.5 w-full">
            {visibleCitations.map((citation, idx) => (
              <button
                key={`${citation.section_id}-${citation.block_id ?? citation.sub_para_id ?? idx}`}
                onClick={() => onOpenCitation(citation)}
                className="text-left border-l-2 border-accent bg-transparent pl-3 pr-2 py-1 cursor-pointer group max-w-full"
              >
                <div className="font-serif italic text-[13px] leading-snug text-ink-2 line-clamp-3 group-hover:text-ink transition-colors">
                  {/* TASK-012 REVISIONE 2 Parte B: the quote is rendered as
                      full inline Markdown (bold/italic/code) -- CSS
                      `line-clamp-3` above does the VISUAL truncation, the
                      server no longer hard-cuts it mid-span. */}
                  “<TutorInlineMarkdown content={citation.quote} />”
                </div>
                <div className="text-[10px] font-mono text-ink-3 mt-0.5 group-hover:text-accent transition-colors">
                  {citation.sub_para_id && citation.title
                    ? `→ ${citation.sub_para_id} — ${citation.title}`
                    : `→ Sezione ${citation.section_id}`}
                </div>
              </button>
            ))}
            {hasMoreCitations && (
              <button
                onClick={() => setCitationsExpanded((expanded) => !expanded)}
                className="self-start text-[11px] font-sans text-ink-3 hover:text-accent bg-transparent border-none cursor-pointer pl-3 underline-offset-2 hover:underline"
              >
                {citationsExpanded
                  ? 'Mostra meno'
                  : `Mostra altre ${message.citations.length - CITATION_COLLAPSE_THRESHOLD}`}
              </button>
            )}
          </div>
        )}

        {message.action != null && <TutorActionCard action={message.action} onRun={onRunAction} />}

        {!isUser && !message.pending && message.persisted && message.content.length > 0 && (
          <div className="flex items-center gap-1">
            <FeedbackButton
              kind="up"
              active={message.feedback === 'up'}
              onClick={() => onFeedback('up')}
            />
            <FeedbackButton
              kind="down"
              active={message.feedback === 'down'}
              onClick={() => onFeedback('down')}
            />
          </div>
        )}
      </div>
    </div>
  )
}
